import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {finalize, Subscription} from 'rxjs';
import {ActivatedRoute, Params} from '@angular/router';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {OrganizationData} from '../../shared/types/organization/organization';
import {OrganizationRestService} from '../../shared/services/oganization/organization-rest.service';
import {OrganizationInputData} from '../../shared/types/organization/organization-input-data';
import {ConfirmationService} from 'primeng/api';
import {FileRestService} from '../../shared/services/common/file/file-rest.service';
import {FileUtilsService} from '../../shared/services/common/file/file-utils.service';
import {Location} from '@angular/common';
import {GlobalMessagesService} from '../../shared/services/messages/global.messages.service';
import {FormHelperService} from '../../shared/services/person/form-helper-service';
import {TranslateService} from '@ngx-translate/core';
import {ListSummaryType} from '../../shared/types/organization/listSummary-type';
import {AppointmentTitleRestService} from '../../shared/services/appointment/appointment-title-rest.service';
import {AppointeeData} from '../../shared/types/appointment/appointee-data';
import {ExtFileInfo} from '../../shared/types/file/ext-file-info';
import * as _ from 'lodash';
import {FileType} from '../../shared/types/file/file-type';
import {ChangeDetectedOrganizationTypeService} from '../../shared/services/oganization/change-organization-type.service';
import {TabType} from '../../shared/types/organization/organization-modify-tabs';
import {FileUpload} from 'primeng/fileupload';
import {HttpErrorResponse} from "@angular/common/http";

@Component({
  selector: 'wsd-organization-modify',
  templateUrl: './organization-modify.component.html',
  styleUrls: ['./organization-modify.component.css']
})
export class OrganizationModifyComponent implements OnInit, OnDestroy {

  @ViewChild('fileUpload')
  fileUpload: FileUpload;

  organization: OrganizationData = {
    appointees: [],
    addresses: [],
    contacts: [],
    summaryList: [],
    interactions: [],
    fileInfo: []
  } as OrganizationData;

  initialAppointments: AppointeeData[];

  parentForm: UntypedFormGroup;
  imageSrc: any;
  deletedImage: boolean;
  currentSummaryListItem: ListSummaryType;
  isSaveDisabled: boolean = false;
  isInitForm: boolean = false;
  selectedTab: TabType = TabType.BASIC;
  tabTypes = TabType;
  private routeSub: Subscription;
  block: boolean;

  constructor(private organizationRestService: OrganizationRestService,
              private appointmentService: AppointmentTitleRestService,
              private confirmService: ConfirmationService,
              private route: ActivatedRoute,
              private globalMessageService: GlobalMessagesService,
              private location: Location,
              private fb: UntypedFormBuilder,
              private fileRestService: FileRestService,
              private fileUtilsService: FileUtilsService,
              private formHelper: FormHelperService,
              private translate: TranslateService,
              private changeDetectedTypeOrganizationService: ChangeDetectedOrganizationTypeService) {
    this.parentForm = this.fb.group({
      organizationType: ['', Validators.required],
      organizationSubtype: [''],
      name: ['', Validators.required],
      nameFr: [''],
      secondaryName: [''],
      secondaryNameFr: [''],
      country: ['', Validators.required],
      order: [''],
      acronym: [''],
      fullAcronym: [''],
      suspensionStatus: [''],
      globalLanguage: [],
      workingLanguage: [],
      currency: [],
      notes: [],
      foundingDate: [],
      active: [false],
      potentialMember: [false],
      wagggsMember: [false],
      wagggsStatus: [false],
      wagggsDescription: [],
      wosmMembershipYear: ['', Validators.pattern('^\\d{4}$')],
      wosmMembership: [],
      pastWosmEvents: [],
      summaryList: [],
      visibleInDirectory: [false],
      federation: [false],
      parentOrganization: [],
      eventCategory: [''],
      feeCategory: [''],
      suspensionInformation: [],
      confidentialInformation: [],
      fileInfo: [[]],
      constitutionReceived: [],
      constitutionApprovalYear: [],
      policyDocuments: [],
      widgets: [],
      sfhAssessmentDate: []
    });
  }

  ngOnInit() {
    this.routeSub = this.route.params.subscribe(
      (params: Params) => this.loadOrganization(params)
    );
  }

  getSummaryDescription(summaryListArray: ListSummaryType[]) {
    const result = summaryListArray.find(
      entry => entry.summaryDate.getFullYear() === new Date().getFullYear()
    );

    this.currentSummaryListItem = result;

    return result ? result.description : '';
  }

  onSelect(event) {
    const file = event.files[0];
    const logoFileInfo = new ExtFileInfo(null, file.name, FileType.ORGANIZATION, null, file, null);
    this.imageSrc = file.objectURL;
    _.remove(this.parentForm.get('fileInfo').value, {'type': 'ORGANIZATION'});
    this.parentForm.get('fileInfo').value.push(logoFileInfo);
    this.fileUpload.clear();
  }

  save() {
    console.log('TU SOM');
    if (!this.parentForm.valid) {
      this.globalMessageService.showErrorMessage(this.formHelper.getFormValidationErrors(this.parentForm),
        this.translate.instant('WS_DIRECTORY.PERSON_MODIFY.VALIDATION_ERRORS'));
      this.formHelper.validateAllFormFields(this.parentForm);
      return;
    }

    if (this.organization.id) {
      this.isSaveDisabled = true;
      const actualConsultantIds = this.organization.appointees.map(title => title.consultant.id);
      const deletedTitles = this.initialAppointments
        .filter(title => !actualConsultantIds.includes(title.consultant.id))
        .map(title => title.consultant.id);

      // We need to check whether non of consultants will be deleted and notify user
      this.appointmentService.checkAppointments(this.organization.id, deletedTitles).subscribe(ids => {
        if (ids.length > 0) {
          this.confirmService.confirm({
            header: this.translate.instant('WS_DIRECTORY.PERSON_MODIFY.APPOINTEE_CONFIRM_HEADER'),
            message: this.translate.instant('WS_DIRECTORY.PERSON_MODIFY.APPOINTEE_CONFIRM_MESSAGE', {count: ids.length}),
            accept: () => this.checkSuspendStatus(),
            reject: () => this.isSaveDisabled = false
          });
        } else {
          this.checkSuspendStatus();
        }
      });
    } else {
      this.checkSuspendStatus();
    }
  }

  prepareSummaryListData(): ListSummaryType[] {
    const summaryList = [];

    if (this.currentSummaryListItem) {
      summaryList.push({
        id: this.currentSummaryListItem.id,
        description: this.parentForm.get('summaryList').value,
        summaryDate: this.currentSummaryListItem.summaryDate,
      });
    } else {
      summaryList.push({
        id: null,
        description: this.parentForm.get('summaryList').value ? this.parentForm.get('summaryList').value : '',
        summaryDate: new Date(),
      });
    }

    return summaryList;
  }

  parentOrganizationData(): number {
    if (this.parentForm.get('parentOrganization').value) {
      return this.parentForm.get('parentOrganization').value.id === -1
        ? null : this.parentForm.get('parentOrganization').value;
    } else {
      return null;
    }
  }

  removeImage() {
    this.imageSrc = null;
    this.deletedImage = true;
    _.remove(this.parentForm.get('fileInfo').value, {'type': 'ORGANIZATION'});
    this.fileUpload.clear();
  }

  updateSubTypes() {
    this.parentForm.get('organizationSubtype').setValue(null);
    this.changeDetectedTypeOrganizationService.changeDetected();
  }

  goBack() {
    this.location.back();
  }

  ngOnDestroy(): void {
    if (this.routeSub) {
      this.routeSub.unsubscribe();
    }
  }

  private loadOrganization(params: Params) {
    this.goToSubStep(params['stepId']);
    if (params['id']) {
      this.organizationRestService.getAllOrganizationData(+params['id']).subscribe(
        (data: OrganizationData) => {
          this.initForm(data);
          this.block = false;
        }
      );
    } else {
      this.block = false;
      this.initForm(this.organization);
    }
  }

  private goToSubStep = (tab: TabType) => this.selectedTab = tab;

  private initForm(data: OrganizationData) {
    this.organization = data;
    this.initialAppointments = [].concat(data.appointees);
    this.parentForm.get('name').setValue(this.organization.name);
    this.parentForm.get('nameFr').setValue(this.organization.nameFr);
    this.parentForm.get('secondaryName').setValue(this.organization.secondaryName);
    this.parentForm.get('secondaryNameFr').setValue(this.organization.secondaryNameFr);
    this.parentForm.get('organizationType').setValue(this.organization.organizationType);
    this.parentForm.get('country').setValue(this.organization.country);
    this.parentForm.get('organizationSubtype').setValue(this.organization.organizationSubtype);
    this.parentForm.get('order').setValue(this.organization.order);
    this.parentForm.get('acronym').setValue(this.organization.acronym);
    this.parentForm.get('fullAcronym').setValue(this.organization.fullAcronym);
    this.parentForm.get('suspensionStatus').setValue(this.organization.suspensionStatus || 'NOT_SUSPENDED');
    this.parentForm.get('globalLanguage').setValue(this.organization.globalLanguage);
    this.parentForm.get('workingLanguage').setValue(this.organization.workingLanguage);
    this.parentForm.get('currency').setValue(this.organization.currency);
    this.parentForm.get('fileInfo').setValue(ExtFileInfo.getExtFileInfo(this.organization.fileInfo));
    this.parentForm.get('federation').setValue(this.organization.federation);
    this.parentForm.get('potentialMember').setValue(this.organization.potentialMember);
    this.parentForm.get('notes').setValue(this.organization.notes);
    this.parentForm.get('wagggsMember').setValue(this.organization.wagggsMember);
    this.parentForm.get('wagggsStatus').setValue(this.organization.wagggsStatus);
    this.parentForm.get('wagggsDescription').setValue(this.organization.wagggsDescription);
    this.parentForm.get('wosmMembershipYear').setValue(this.organization.wosmMembershipYear);
    this.parentForm.get('wosmMembership').setValue(this.organization.wosmMembership);
    this.parentForm.get('pastWosmEvents').setValue(this.organization.pastWosmEvents);
    this.parentForm.get('summaryList').setValue(this.getSummaryDescription(this.organization.summaryList));
    this.parentForm.get('visibleInDirectory').setValue(this.organization.visibleInDirectory);
    this.parentForm.get('foundingDate').setValue(this.organization.foundingDate);
    this.parentForm.get('parentOrganization').setValue(this.organization.parentOrganization);
    this.parentForm.get('eventCategory').setValue(this.organization.eventCategory);
    this.parentForm.get('feeCategory').setValue(this.organization.feeCategory);
    this.parentForm.get('suspensionInformation').setValue(this.organization.suspensionInformation);
    this.parentForm.get('confidentialInformation').setValue(this.organization.confidentialInformation);
    this.parentForm.get('constitutionReceived').setValue(this.organization.constitutionReceived);
    this.parentForm.get('constitutionApprovalYear').setValue(this.organization.constitutionApprovalYear);
    this.parentForm.get('policyDocuments').setValue(this.organization.policyDocuments);
    this.parentForm.get('sfhAssessmentDate').setValue(this.organization.sfhAssessmentDate);
    this.loadLogo();
    this.isInitForm = true;
  }

  private loadLogo() {
    const logo = this.organization.fileInfo
      .filter(Boolean)
      .find(file => file.type === FileType.ORGANIZATION);
    if (logo) {
      this.loadImage(logo.id);
    }
  }

  private loadImage(imgId: number) {
    if (!imgId) {
      return;
    }
    this.fileRestService.getImage(imgId.toString()).subscribe(
      ((data: any) => {
        this.imageSrc = this.fileUtilsService.getImage(data.content);
        this.deletedImage = false;
      })
    );
  }

  private checkSuspendStatus() {
    if (this.organization.suspensionStatus !== 'FULL' && this.parentForm.get('suspensionStatus').value === 'FULL') {
      this.confirmService.confirm({
        header: this.translate.instant('WS_DIRECTORY.ORG_DETAIL.SUSPEND_CONFIRM_HEADER'),
        message: this.translate.instant('WS_DIRECTORY.ORG_DETAIL.SUSPEND_CONFIRM_MESSAGE'),
        accept: () => this.performSafe(),
        reject: () => this.isSaveDisabled = false
      });
    } else {
      this.performSafe();
    }
  }

  private saveInteractions() {
    this.organizationRestService.updateInteraction(this.organization.id, this.organization.interactions).subscribe(
      () => {
        this.globalMessageService.showSuccessMessage(this.translate.instant('WS_DIRECTORY.ORG_MODIFY.SAVE_INTERACTION_MESSAGE'));
        this.organization.interactions = null;
        this.block = true;
        this.routeSub = this.route.params.subscribe(
          (params: Params) => this.loadOrganization(params)
        );
      },
      () => {
        this.globalMessageService.showErrorMessage(this.translate.instant('WS_DIRECTORY.ORG_MODIFY.SAVE_ERROR_MESSAGE'));
      },
      () => this.isSaveDisabled = false
    );
  }

  private saveWidgets()  {
    this.organizationRestService.updateWidgets(this.organization.id, this.organization.widgets).subscribe(
      () => {
        this.globalMessageService.showSuccessMessage(this.translate.instant('WS_DIRECTORY.ORG_MODIFY.SAVE_WIDGETS_MESSAGE'));
        this.organization.interactions = null;
        this.block = true;
        this.routeSub = this.route.params.subscribe(
          (params: Params) => this.loadOrganization(params)
        );
      },
      () => {
        this.globalMessageService.showErrorMessage(this.translate.instant('WS_DIRECTORY.ORG_MODIFY.SAVE_ERROR_MESSAGE'));
      },
      () => this.isSaveDisabled = false
    );
  }


  private performSafe() {
    this.organizationRestService.save(this.prepareData(), ExtFileInfo.getNewFiles(this.parentForm.value.fileInfo)).pipe(
      finalize(() => (this.isSaveDisabled = false)) // Ensures re-enabling on completion
    )
      .subscribe(
        () => this.goBack(),
        (error: HttpErrorResponse) => {
          if (error.status === 413) {
            this.globalMessageService.showErrorMessage(
              this.translate.instant('WS_DIRECTORY.ORG_MODIFY.FILE_SIZE_ERROR_MESSAGE')
            );
          } else {
            this.globalMessageService.showErrorMessage(
              this.translate.instant('WS_DIRECTORY.ORG_MODIFY.SAVE_ERROR_MESSAGE')
            );
          }
        }
      );
  }

  private prepareData(): OrganizationInputData {
    return {
      id: this.organization.id,
      name: this.parentForm.get('name').value,
      nameFr: this.parentForm.get('nameFr').value,
      secondaryName: this.parentForm.get('secondaryName').value,
      secondaryNameFr: this.parentForm.get('secondaryNameFr').value,
      organizationType: this.parentForm.get('organizationType').value ? this.parentForm.get('organizationType').value.id : null,
      organizationSubtype: this.parentForm.get('organizationSubtype').value ? this.parentForm.get('organizationSubtype').value.id : null,
      countryId: this.parentForm.get('country').value ? this.parentForm.get('country').value.id : null,
      acronym: this.parentForm.get('acronym').value,
      fullAcronym: this.parentForm.get('fullAcronym').value,
      order: this.parentForm.get('order').value,
      addresses: this.organization.addresses,
      appointees: this.organization.appointees,
      interactions: this.organization.interactions,
      contacts: this.organization.contacts,
      globalLanguage: this.parentForm.get('globalLanguage').value ? this.parentForm.get('globalLanguage').value.id : null,
      workingLanguage: this.parentForm.get('workingLanguage').value ? this.parentForm.get('workingLanguage').value.id : null,
      notes: this.parentForm.get('notes').value,
      foundingDate: this.parentForm.get('foundingDate').value,
      federation: this.parentForm.get('federation').value,
      wagggsMember: this.parentForm.get('wagggsMember').value,
      wagggsStatus: this.parentForm.get('wagggsStatus').value,
      wagggsDescription: this.parentForm.get('wagggsDescription').value,
      suspensionStatus: this.parentForm.get('suspensionStatus').value,
      suspensionInformation: this.parentForm.get('suspensionInformation').value,
      confidentialInformation: this.parentForm.get('confidentialInformation').value,
      eventCategory: this.parentForm.get('eventCategory').value,
      feeCategory: this.parentForm.get('feeCategory').value,
      currencyId: this.parentForm.get('currency').value ? this.parentForm.get('currency').value.id : null,
      potentialMember: this.parentForm.get('potentialMember').value,
      wosmMembershipYear: this.parentForm.get('wosmMembershipYear').value,
      wosmMembership: this.parentForm.get('wosmMembership').value,
      pastWosmEvents: this.parentForm.get('pastWosmEvents').value,
      summaryList: this.prepareSummaryListData(),
      visibleInDirectory: this.parentForm.get('visibleInDirectory').value,
      parentOrganization: this.parentOrganizationData(),
      removeImage: this.deletedImage,
      fileInfo: this.parentForm.get('fileInfo').value,
      constitutionReceived: this.parentForm.get('constitutionReceived').value,
      constitutionApprovalYear: this.parentForm.get('constitutionApprovalYear').value,
      policyDocuments: this.parentForm.get('policyDocuments').value,
      widgets: this.parentForm.get('widgets').value,
      sfhAssessmentDate: this.parentForm.get("sfhAssessmentDate").value
    };
  }
}
